/**
 * Vim Motion System Types
 * Defines the contract for vim-like navigation
 */

export type VimMode = 'normal' | 'insert' | 'visual' | 'command';

export interface VimState {
  enabled: boolean;
  mode: VimMode;
  commandBuffer: string;
  lastCommand: string;
  visualStart: number | null;
  currentElement: HTMLElement | null;
  yankBuffer: string;
  searchBuffer: string;
  isSearching: boolean;
  searchDirection: 'forward' | 'backward';
  repeatCount: number;
  showHelp: boolean;
}

export interface VimPosition {
  x: number;
  y: number;
  element: HTMLElement | null;
}

export type VimCommand =
  | 'moveUp'
  | 'moveDown'
  | 'moveLeft'
  | 'moveRight'
  | 'moveWordForward'
  | 'moveWordBackward'
  | 'moveLineStart'
  | 'moveLineEnd'
  | 'moveDocumentStart'
  | 'moveDocumentEnd'
  | 'scrollHalfPageUp'
  | 'scrollHalfPageDown'
  | 'scrollPageUp'
  | 'scrollPageDown'
  | 'click'
  | 'yank'
  | 'yankLine'
  | 'delete'
  | 'deleteLine'
  | 'paste'
  | 'undo'
  | 'redo'
  | 'enterInsert'
  | 'enterNormal'
  | 'enterCommand'
  | 'searchForward'
  | 'searchBackward'
  | 'nextSearchResult'
  | 'prevSearchResult'
  | 'toggleHelp'
  | 'none';

export interface VimAction {
  command: VimCommand;
  count: number;
  register?: string;
}

export interface VimHint {
  key: string;
  label: string;
}

export const VIM_HINTS: VimHint[] = [
  { key: 'h', label: '← left' },
  { key: 'j', label: '↓ down' },
  { key: 'k', label: '↑ up' },
  { key: 'l', label: '→ right' },
  { key: 'gg', label: 'top' },
  { key: 'G', label: 'bottom' },
  { key: '/', label: 'search' },
  { key: 'n', label: 'next' },
  { key: 'N', label: 'prev' },
  { key: 'i', label: 'input' },
  { key: 'Esc', label: 'normal' },
  { key: '?', label: 'help' },
];

export const VIM_KEY_MAP: Record<string, VimCommand> = {
  h: 'moveLeft',
  j: 'moveDown',
  k: 'moveUp',
  l: 'moveRight',
  w: 'moveWordForward',
  b: 'moveWordBackward',
  '0': 'moveLineStart',
  '^': 'moveLineStart',
  $: 'moveLineEnd',
  g: 'moveDocumentStart',
  G: 'moveDocumentEnd',
  d: 'scrollHalfPageDown',
  u: 'scrollHalfPageUp',
  'ctrl-d': 'scrollHalfPageDown',
  'ctrl-u': 'scrollHalfPageUp',
  'ctrl-f': 'scrollPageDown',
  'ctrl-b': 'scrollPageUp',
  'ctrl-r': 'redo',
  i: 'enterInsert',
  ':': 'enterCommand',
  '/': 'searchForward',
  '?': 'searchBackward',
  n: 'nextSearchResult',
  N: 'prevSearchResult',
  Enter: 'click',
  y: 'yank',
  Y: 'yankLine',
  p: 'paste',
  x: 'delete',
  D: 'deleteLine',
};
